import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FeatureFlagsProvider,
  RootProvider,
  useFeatureFlagsContext,
  useI18n,
} from '@jarvis/react-portal-addons';
import { IconDevicesLaptopPrinter, IconPeople, IconSmartApp } from '@veneer/core';
import {
  Body, ContentContainer,
  DownloadBarContainer, DownloadLeftContent, DownloadLink, DownloadText,
  Item, ItemBody, ItemLeftContent, ItemRightContent, ItemTitle,
  LandingPageContainer, PageContainer, Title,
} from './styles';
import Images from '../../assets/images';
import resources from '../../assets/locale/index';
import useOpenStoreForHpApp from '../../hooks/useOpenStoreForHpApp';
import { LandingPageScreenDisplayed, publishEvent } from '../../utils/analytics';

const LandingPageContent = () => {
  const { t } = useI18n();
  const { landingPageDownloadNow } = useFeatureFlagsContext();
  const { openStoreForHpApp, hpAppUrl } = useOpenStoreForHpApp();

  const handleDownloadClick = useCallback(() => {
    openStoreForHpApp();
    return false;
  }, [openStoreForHpApp]);

  useEffect(() => {
    publishEvent(LandingPageScreenDisplayed);
  }, []);

  return (
    <PageContainer>
      {landingPageDownloadNow && (
        <DownloadBarContainer>
          <DownloadLeftContent>
            <IconSmartApp size={20} data-testid="download-icon" />
            <DownloadText
              className="label-small"
              data-testid="download-text"
            >
              {t('landing-page.download-text')}
            </DownloadText>
          </DownloadLeftContent>
          <DownloadLink
            href={hpAppUrl}
            onClick={handleDownloadClick}
            rel="noopener noreferrer"
            className="label-small"
            data-testid="download-link"
          >
            {t('landing-page.download-link')}
          </DownloadLink>
        </DownloadBarContainer>
      )}
      <LandingPageContainer>
        <ContentContainer>
          <Title
            className="title-regular"
            data-testid="landing-page-title"
          >
            {t('landing-page.title')}
          </Title>
          <Body
            className="body-large"
            data-testid="landing-page-body"
          >
            {t('landing-page.body')}
          </Body>
          <Item>
            <ItemLeftContent>
              <img
                data-testid="subscriptions-icon"
                src={Images.manageSubscriptions}
                alt=""
              />
            </ItemLeftContent>
            <ItemRightContent>
              <ItemTitle
                className="subtitle-regular"
                data-testid="subscriptions-title"
              >
                {t('landing-page.subscriptions.title')}
              </ItemTitle>
              <ItemBody
                className="caption-regular"
                data-testid="subscriptions-body"
              >
                {t('landing-page.subscriptions.body')}
              </ItemBody>
            </ItemRightContent>
          </Item>
          <Item>
            <ItemLeftContent>
              <IconDevicesLaptopPrinter
                data-testid="device-icon"
                size={28}
              />
            </ItemLeftContent>
            <ItemRightContent>
              <ItemTitle
                className="subtitle-regular"
                data-testid="device-title"
              >
                {t('landing-page.device.title')}
              </ItemTitle>
              <ItemBody
                className="caption-regular"
                data-testid="device-body"
              >
                {t('landing-page.device.body')}
              </ItemBody>
            </ItemRightContent>
          </Item>
          <Item>
            <ItemLeftContent>
              <IconPeople
                data-testid="share-icon"
                size={28}
              />
            </ItemLeftContent>
            <ItemRightContent>
              <ItemTitle
                className="subtitle-regular"
                data-testid="share-title"
              >
                {t('landing-page.share.title')}
              </ItemTitle>
              <ItemBody
                className="caption-regular"
                data-testid="share-body"
              >
                {t('landing-page.share.body')}
              </ItemBody>
            </ItemRightContent>
          </Item>
        </ContentContainer>
      </LandingPageContainer>
    </PageContainer>
  );
};

const LandingPage = props => {
  const { shell, stack } = props;
  const localization = shell?.localization || {};
  const flags = useMemo(
    () => [
      { key: 'landingPageDownloadNow' },
    ],
    [],
  );

  return (
    <FeatureFlagsProvider
      clientKey="smb-portal"
      flags={flags}
    >
      <RootProvider
        stack={stack}
        shell={shell}
        localization={localization}
        resources={resources}
      >
        <LandingPageContent />
      </RootProvider>
    </FeatureFlagsProvider>
  );
};

LandingPage.defaultProps = {
  shell: {},
};

LandingPage.propTypes = {
  shell: PropTypes.objectOf(PropTypes.any),
  stack: PropTypes.number.isRequired,
};

export default LandingPage;
